import { useState, useEffect, useCallback, useRef } from 'react';

// Skloňování počtu položek — do hlášky „Vybrány 3 položky".
export const pocetPolozek = (n) => `${n} ${n === 1 ? 'položka' : (n >= 2 && n <= 4 ? 'položky' : 'položek')}`;

/**
 * Vícenásobný výběr řádků v seznamu receptáře.
 *
 * Ctrl/Cmd + klik přidává a odebírá z výběru, obyčejný klik výběr zruší a otevře
 * detail (tj. chování, na jaké je člověk zvyklý ze správce souborů). Přetažení
 * vybraného řádku bere s sebou celý výběr, přetažení nevybraného jen ten jeden.
 *
 * `resetKey` = cokoliv, po jehož změně nemá výběr smysl (přepnutí typu receptů,
 * záložky…) — výběr se vynuluje.
 */
export function useVyberPolozek(resetKey) {
    const [vybrane, setVybrane] = useState(() => new Set());

    useEffect(() => { setVybrane(new Set()); }, [resetKey]);

    // Escape = zrušit výběr (bez něj se z něj dá vycouvat jen klikem na řádek).
    useEffect(() => {
        if (vybrane.size === 0) return;
        const h = (e) => { if (e.key === 'Escape') setVybrane(new Set()); };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [vybrane.size]);

    // Prázdný výběr nechává stejnou instanci — ať obyčejný klik zbytečně
    // nepřekresluje celý seznam.
    const zrus = useCallback(() => setVybrane(prev => (prev.size ? new Set() : prev)), []);

    const klikNaRadek = useCallback((e, id, otevriDetail) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            setVybrane(prev => {
                const n = new Set(prev);
                if (n.has(id)) n.delete(id); else n.add(id);
                return n;
            });
            return;
        }
        setVybrane(prev => (prev.size ? new Set() : prev));
        otevriDetail?.();
    }, []);

    // Které id se reálně táhnou — vybraný řádek táhne celý výběr.
    const taheneIds = useCallback((id) => (vybrane.has(id) ? [...vybrane] : [id]), [vybrane]);

    return { vybrane, zrus, klikNaRadek, taheneIds };
}

/**
 * Náhled při tažení víc položek — místo obrázku jednoho řádku bublina s počtem.
 * Prvek musí být v DOM, než ho prohlížeč vyfotí, proto se uklízí až v dalším tiku.
 */
export function nastavNahledTazeni(e, pocet) {
    if (pocet <= 1) return;
    const el = document.createElement('div');
    el.textContent = pocetPolozek(pocet);
    el.style.cssText = [
        'position:fixed', 'top:-1000px', 'left:-1000px',
        'padding:7px 14px', 'border-radius:999px',
        'font:600 12px/1 system-ui,-apple-system,sans-serif',
        'background:#3B82F6', 'color:#fff', 'white-space:nowrap',
        'box-shadow:0 6px 18px rgba(0,0,0,0.35)',
    ].join(';');
    document.body.appendChild(el);
    try { e.dataTransfer.setDragImage(el, 12, 12); } catch { /* noop */ }
    setTimeout(() => el.remove(), 0);
}
